/**
 * DOCUMENTOS DE LICITAÇÕES: A NOVA VERSÃO (27/10/2026, decisões do Heitor: A1, C1, M1, D1; V1 é do backend, em PR própria).
 *
 * A ação de rotina da linha é subir a versão nova do documento, e ela ficava atrás do "Editar". Aqui moram as regras puras:
 * qual botão a linha mostra, por qual caminho a versão nova sobe e o que a confirmação diz da anterior.
 *
 * DOIS CAMINHOS (backend #540 e #541, no ar desde 27/10/2026):
 *  - `por_tipo`: o "renovar por upload" (POST /documents/renew-upload) cria um documento NOVO e desativa os ativos do MESMO
 *    TIPO, guardando-os (isActive=false). Desde a #541 só vale para certidão (uma por tipo) e MOVE o link público quando o
 *    antecessor com link é único (com mais de um, não move e devolve `linkAmbiguo`).
 *  - `por_versao`: os demais (atestado, contrato, "outro"…) sobem pela rota de versão do documento
 *    (POST /documents/:id/versions, #540): desativa SÓ este documento, guarda o arquivo anterior, grava o elo
 *    (previousVersionId) e devolve `anterior.linkPublicoMovido`. Usar o `por_tipo` num atestado desativaria os 32 de uma vez.
 */

/** Certidões que o robô emite e grava sozinho (o mesmo do "Renovar Certidão" do topo). */
export const TIPOS_COM_ROBO = ['cndt', 'cicad_estadual', 'cnd_tcu', 'fgts'] as const;
/** Certidões sem robô: abre o site do órgão e, na volta, sobe o PDF emitido (o mesmo do "Renovar Certidão" do topo). */
export const TIPOS_PELO_SITE = [
  'certidao_negativa_estadual', 'certidao_negativa_municipal', 'cicad_municipal', 'certidao_negativa_federal',
  'cnd_cgu', 'certidao_junta_comercial',
] as const;

export type ModoDaAcao = 'robo' | 'site' | 'arquivo';
export type CaminhoDaVersao = 'por_tipo' | 'por_versao';

const ROBO = new Set<string>(TIPOS_COM_ROBO);
const SITE = new Set<string>(TIPOS_PELO_SITE);

/** A1: o botão da linha. Certidão (robô ou site) = "Renovar"; o resto = "Nova versão". */
export function acaoPrincipalDoDocumento(doc: { type?: string | null }): { rotulo: 'Renovar' | 'Nova versão'; modo: ModoDaAcao } {
  const t = String(doc?.type || '');
  if (ROBO.has(t)) return { rotulo: 'Renovar', modo: 'robo' };
  if (SITE.has(t)) return { rotulo: 'Renovar', modo: 'site' };
  return { rotulo: 'Nova versão', modo: 'arquivo' };
}

/**
 * Por onde a versão nova sobe (ver o cabeçalho). ⛔ O caminho por tipo desativa TODOS os ativos do tipo: só vale com UM
 * ativo dele. Em 27/10/2026 os 10 tipos de certidão tinham exatamente 1 ativo cada (medido), mas isso é dado, não
 * garantia — com mais de um, sobe pela rota de versão, que só toca este documento.
 */
export function caminhoDaNovaVersao(doc: { type?: string | null }, ativosDoMesmoTipo = 1): CaminhoDaVersao {
  const t = String(doc?.type || '');
  return (ROBO.has(t) || SITE.has(t)) && ativosDoMesmoTipo <= 1 ? 'por_tipo' : 'por_versao';
}

/** O que a confirmação diz da versão atual (desde a #540/#541, nos dois caminhos ela fica guardada). */
export function avisoDaAnterior(_caminho: CaminhoDaVersao, quando?: string | null): string {
  const da = quando ? ` (emitida em ${quando})` : '';
  return `A versão atual${da} não é apagada: fica guardada como versão anterior.`;
}

/**
 * O link público (decisão do Heitor, 27/10/2026: a versão nova HERDA o link, e o link já distribuído passa a servir a nova).
 * Desde a #540/#541 o servidor MOVE o link nos dois caminhos (no por tipo, quando o antecessor com link é único — e a tela
 * só usa esse caminho com um ativo do tipo). O caso ambíguo vem na resposta e é dito depois, por `avisoDoLinkAmbiguo`.
 */
export function avisoDoLink(_caminho: CaminhoDaVersao, temLink: boolean): string | null {
  return temLink ? 'O link público passa a mostrar esta versão.' : null;
}

/** O renew-upload não move o link quando mais de um anterior tinha link (`linkAmbiguo`, #541): a tela diz, não cala. */
export function avisoDoLinkAmbiguo(linkAmbiguo: unknown): string | null {
  const ids = Array.isArray(linkAmbiguo) ? linkAmbiguo : [];
  return ids.length
    ? `O link público não foi movido: ${ids.length} versões anteriores tinham link (${ids.map((i) => `#${i}`).join(', ')}). Gere o link na versão nova pelo ⋯.`
    : null;
}

/** Depois de subir pela rota de versão: o servidor diz se o link foi movido, e a tela repete (não presume). */
export function avisoDoLinkMovido(resposta: { anterior?: { linkPublicoMovido?: boolean } } | null | undefined): string | null {
  return resposta?.anterior?.linkPublicoMovido ? 'O link público já aponta para esta versão.' : null;
}

/**
 * Os 409 da rota de versão, em frase (o código vem no corpo: `codigo`). Fora deles, a mensagem do servidor, como antes.
 * ⚠️ `VERSAO_JA_EXISTE` costuma ser o segundo clique ou a tela velha: a versão nova JÁ está no servidor.
 */
export function mensagemDoErroDaVersao(e: any): string {
  const corpo = e?.response?.data || {};
  if (corpo.codigo === 'VERSAO_ANTIGA') {
    return 'Este documento já é uma versão anterior: a versão nova sobe a partir do documento atual. Recarregue a lista e confira.';
  }
  if (corpo.codigo === 'VERSAO_JA_EXISTE') {
    return 'Este documento já tem uma versão mais nova no servidor (talvez de outro envio). Recarregue a lista e confira antes de subir de novo.';
  }
  return `Não subiu a nova versão: ${corpo.message || e?.message || 'erro desconhecido'}. Nada mudou no documento.`;
}

/** Quem subiu a versão: a trilha não tem autor nos documentos anteriores a 27/10/2026, e a tela diz isso em vez de deixar vazio. */
export function autorDaVersao(criadoPor: string | null | undefined): string {
  return criadoPor || 'não registrado';
}

export interface DatasLidas { issueDate?: string | null; expiryDate?: string | null }

/**
 * D1: as datas da versão nova. Lidas do arquivo quando a leitura deu; senão, pedidas — nunca as da versão anterior caladas
 * (era o que acontecia quando a leitura falhava: o formulário ficava com as datas velhas).
 */
export function datasDaNovaVersao(lidas: DatasLidas | null, semValidade: boolean): {
  issueDate: string; expiryDate: string; origem: 'lidas_do_arquivo' | 'parcial' | 'nao_lidas';
} {
  const issue = lidas?.issueDate || '';
  const expiry = semValidade ? '' : (lidas?.expiryDate || '');
  const temTudo = !!issue && (semValidade || !!expiry);
  const temAlgo = !!issue || !!expiry;
  return { issueDate: issue, expiryDate: expiry, origem: temTudo ? 'lidas_do_arquivo' : temAlgo ? 'parcial' : 'nao_lidas' };
}

/** Pode subir: arquivo escolhido, emissão e (validade ou "não se aplica"). */
export function podeSubirNovaVersao(f: { arquivo: unknown; issueDate: string; expiryDate: string; semValidade: boolean }): boolean {
  return !!f.arquivo && !!f.issueDate && (f.semValidade || !!f.expiryDate);
}
